import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';

export type SkuAlphabetFilterProps = {
  selectedLetter: string | null | undefined;
  onLetterSelect: (letter: string | null) => void;
  availableLetters?: Set<string>;
  selectedSearchVariant?: { variantId: string; productId: string; sku: string } | null;
  onClearSearchSelection?: () => void;
};

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

export function SkuAlphabetFilter({
  selectedLetter,
  onLetterSelect,
  availableLetters,
  selectedSearchVariant,
  onClearSearchSelection,
}: SkuAlphabetFilterProps) {
  return (
    <div className="w-full px-2 py-1">
      {/* Mobile: Horizontal scroll, Desktop: Wrap */}
      <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide sm:flex-wrap pb-1 w-full">
        {selectedSearchVariant ? (
          <div className="flex items-center justify-between w-full bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-emerald-800">Selected Product:</span>
              <span className="text-sm font-bold text-emerald-700 bg-white px-2 py-0.5 rounded border border-emerald-100 shadow-sm">
                {selectedSearchVariant.sku}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-3 text-slate-600 hover:bg-emerald-100 hover:text-slate-900 font-medium"
              onClick={onClearSearchSelection}
              title="Clear search selection"
            >
              <X className="h-4 w-4 mr-1.5" />
              Clear Search
            </Button>
          </div>
        ) : (
          <>
            {/* All button */}
            <Button
              variant={selectedLetter === null ? 'default' : 'outline'}
              size="sm"
              className={cn(
                'h-6 min-w-[36px] px-2 text-[10px] font-semibold flex-shrink-0',
                selectedLetter === null ? 'bg-blue-500 hover:bg-blue-600' : 'hover:bg-slate-100',
              )}
              onClick={() => onLetterSelect(null)}
            >
              All
            </Button>

            {/* Alphabet buttons */}
            {ALPHABET.map((letter) => {
              const isAvailable = !availableLetters || availableLetters.has(letter);
              const isSelected = selectedLetter === letter;

              return (
                <Button
                  key={letter}
                  variant={isSelected ? 'default' : 'outline'}
                  size="sm"
                  className={cn(
                    'h-6 w-6 p-0 text-[10px] font-semibold flex-shrink-0',
                    isSelected && 'bg-blue-500 hover:bg-blue-600',
                    !isAvailable && 'opacity-30 cursor-not-allowed',
                  )}
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  onClick={() => {
                    if (isAvailable) {
                      if (isSelected) {
                        onLetterSelect(null);
                      } else {
                        onLetterSelect(letter);
                      }
                    }
                  }}
                  disabled={!isAvailable}
                >
                  {letter}
                </Button>
              );
            })}

            {/* Clear filter button */}
            {selectedLetter && (
              <Button
                variant="outline"
                size="sm"
                className="h-6 w-6 p-0 hover:bg-red-50 hover:text-red-600 hover:border-red-300 flex-shrink-0"
                onClick={() => onLetterSelect(null)}
                title="Clear filter"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
